'use client';

import React, { useEffect } from 'react';

import { deleteCookie, getCookie } from 'cookies-next/client';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { LoginActions } from './LoginActions';

import { ContextualSignupModalV2 } from '@/components/ContextualSignupModal/ContextualSignupModalV2';
import { ACCESS_TOKEN_KEY } from '@/constants/cookies';
import { buildStaticUrl } from '@/constants/endpoints';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config.ts';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { type AccountLinkingCredential } from '@/modules/login/types';
import { useLoginTracking } from '@/modules/login/useLoginTracking';
import { useExtendedRouter } from '@/modules/routing/useExtendedRouter.ts';
import {
  COOKIE_ZENDESK_SSO_STATE,
  RETURN_TO_PARAM,
  ZD_SSO_PARAM,
} from '@/modules/zendesk/constants.ts';
import {
  getZendeskRedirectUrl,
  setZendeskSSOStateCookie,
} from '@/modules/zendesk/helpers';

export type Props = {
  nonce?: string;
  storedAccountLinkingCredential?: AccountLinkingCredential;
  combinedFlow?: boolean;
};

export function Login({
  nonce = '',
  storedAccountLinkingCredential,
  combinedFlow = false,
}: Props) {
  const { sendLoginViewEvent } = useLoginTracking();
  const searchParams = useSearchParams();
  const router = useExtendedRouter();
  const t = useTranslations('product');

  const isBuyNowRedirect = searchParams.get('buyNow') === 'true';

  const [{ web_8651_buy_now_presignup }] = useExperiments(
    ['web_8651_buy_now_presignup'],
    { deferred: true }
  );

  useEffect(() => {
    sendLoginViewEvent();

    // If user is already logged in and we have Zendesk SSO params, handle the flow
    // We shouldn't really ever reach here.
    const accessTokenCookie = getCookie(ACCESS_TOKEN_KEY);
    if (accessTokenCookie && searchParams.get(ZD_SSO_PARAM) === 'true') {
      const returnTo = searchParams.get(RETURN_TO_PARAM) || '';
      deleteCookie(COOKIE_ZENDESK_SSO_STATE);
      const zendeskRedirect = getZendeskRedirectUrl(returnTo);
      router.push(zendeskRedirect);
      return;
    }

    setZendeskSSOStateCookie(searchParams);
  }, []);

  return (
    <>
      {isBuyNowRedirect && web_8651_buy_now_presignup === VARIANT_IDENTIFIER ? (
        <ContextualSignupModalV2
          headingText={t('ContextualSignup.Heading')}
          bodyText={t('ContextualSignup.Text')}
          image={buildStaticUrl('/web/assets/illustrations/shopping-bag.png')}
          combinedFlow={combinedFlow}
        >
          <LoginActions
            nonce={nonce}
            storedAccountLinkingCredential={storedAccountLinkingCredential}
          />
        </ContextualSignupModalV2>
      ) : (
        <LoginActions
          nonce={nonce}
          storedAccountLinkingCredential={storedAccountLinkingCredential}
        />
      )}
    </>
  );
}
